type RouteParam = string | string[] | null | undefined;

/**
 * The My Tabbio hub: identity block + the CV list. It is the CV stack's initial
 * route (`unstable_settings.initialRouteName` in `(app)/cv/_layout.tsx`), so
 * every CV editor is pushed above it and Back from an editor lands here, even
 * when the editor was reached by a cold deep link.
 *
 * `MY_TABBIO_HREF` is the drawer/sidebar form (with the route group), which
 * `sidebar.ts` repeats as a literal because the tour test pins hrefs by text.
 */
export const MY_TABBIO_PATH = "/cv/my-tabbio";
export const MY_TABBIO_HREF = "/(app)/cv/my-tabbio";

export function buildMyTabbioRoute() {
  return MY_TABBIO_PATH as any;
}

/**
 * Editor Back has an explicit destination. A CV can be opened from a chat
 * card or external link, where generic history may point somewhere else.
 * dismissTo pops to an existing hub, or replaces the editor when none exists.
 */
export function leaveCvEditor(
  router: { dismissTo: (href: any) => void },
  returnTo?: RouteParam,
) {
  const destination = safeCvReturnRoute(returnTo) ?? buildMyTabbioRoute();
  router.dismissTo(destination);
}

/** Only routes created by the app may control the editor's return destination. */
function safeCvReturnRoute(value?: RouteParam) {
  const route = normalizeRouteParam(value);
  if (!route || !route.startsWith('/jobs/') || route.includes('://')) return null;
  const [path, query] = route.split('?');
  if (!path || path === '/jobs/' || (query && !/^tailoredCvId=[^&]+$/.test(query))) return null;
  return route as any;
}

export function buildCvRoute(pathname: string, target?: string | null) {
  const visiblePathname = stripLeadingExpoRouteGroup(pathname);
  if (!target) return visiblePathname as any;

  const grantId = readGrantTargetId(target);
  if (grantId && isCvPath(visiblePathname)) {
    const suffix = visiblePathname.slice("/cv".length);
    return `/cv/access/${encodeURIComponent(grantId)}${suffix}` as any;
  }

  return `${visiblePathname}?cvTarget=${encodeURIComponent(target)}` as any;
}

export function buildTailoredCvRoute(id?: RouteParam, suffix?: string | null) {
  const cvId = normalizeRouteParam(id);
  const routeSuffix = normalizeRouteSuffix(suffix);

  if (!cvId) {
    return `/cv/tailored${routeSuffix}` as any;
  }

  return `/cv/tailored/${encodeURIComponent(cvId)}${routeSuffix}` as any;
}

export function closeCvShareRoute(router: {
  back: () => void;
  canGoBack: () => boolean;
  dismissTo: (href: any) => void;
}, returnTo?: RouteParam) {
  if (normalizeRouteParam(returnTo) === 'my-tabbio' || !router.canGoBack()) {
    router.dismissTo(buildMyTabbioRoute());
  } else {
    router.back();
  }
}

export function buildTailoredCvCreateRoute(
  audience?: "me" | "someone_else" | null,
  params?: {
    /**
     * Step-1 source. `ai` is the legacy value kept for existing entry points
     * (jobs, the resume-import notice); the create screen maps it onto
     * "my profile + pasted job description".
     */
    source?: "upload" | "ai" | "blank" | "existing" | null;
    /** Preselects which CV step 1 starts from (implies source `existing`). */
    sourceCvId?: string | null;
    /** Preselects a CV skill in step 2. */
    skillId?: string | null;
    title?: string | null;
    prompt?: string | null;
  } | null,
) {
  const base = "/cv/tailored/create";
  const queryParams: [string, string][] = [];

  if (audience && audience !== "me") {
    queryParams.push(["audience", audience]);
  }
  if (params?.source) {
    queryParams.push(["source", params.source]);
  }
  if (params?.sourceCvId) {
    queryParams.push(["sourceCvId", params.sourceCvId]);
  }
  if (params?.skillId) {
    queryParams.push(["skillId", params.skillId]);
  }
  if (params?.title) {
    queryParams.push(["title", params.title]);
  }
  if (params?.prompt) {
    queryParams.push(["prompt", params.prompt]);
  }

  if (!queryParams.length) return base as any;

  const query = queryParams
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return `${base}?${query}` as any;
}

export function stripLeadingExpoRouteGroup(pathname: string) {
  const normalized = pathname.trim() || "/";
  return (
    normalized
      .replace(/^\/\((?:app|auth|employer|onboarding)\)(?=\/|$)/, "")
      .replace(/^\/%28(?:app|auth|employer|onboarding)%29(?=\/|$)/i, "") || "/"
  );
}

export function resolveCvRouteTarget(params: {
  cvTarget?: RouteParam;
  grantId?: RouteParam;
  target?: RouteParam;
}) {
  const grantId = normalizeRouteParam(params.grantId);
  if (grantId) return `grant:${grantId}`;
  return normalizeRouteParam(params.cvTarget ?? params.target);
}

export function readGrantTargetId(target: string) {
  if (target.startsWith("grant:")) return target.slice("grant:".length);
  if (target.startsWith("access:")) return target.slice("access:".length);
  return null;
}

function normalizeRouteParam(value: RouteParam) {
  const raw = Array.isArray(value) ? value[0] : value;
  const normalized = raw?.trim();
  return normalized || null;
}

function normalizeRouteSuffix(value?: string | null) {
  const normalized = value?.trim().replace(/^\/+|\/+$/g, "");
  return normalized ? `/${normalized}` : "";
}

function isCvPath(pathname: string) {
  return pathname === "/cv" || pathname.startsWith("/cv/");
}
